import { ReactElement, useMemo, useState } from 'react';
import { DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  ForecastModelSummaryData,
  getCombinedChannelPlatform,
  TrainTestData
} from '@blisspointmedia/bpm-types/dist/Forecasting';
import { ChartContainer } from '../../../Components/ChartContainer';
import { ActualVsPredicted } from '../../../Components/Charts/ActualVsPredicted/ActualVsPredicted';
import { AreaChart } from '../../../Components/Charts/AreaChart';
import { Design, Placement, Size } from '../../../Components/designConstants';
import { DownloadDropdown } from '../../../Components/DownloadDropdown';
import { Dropdown } from '../../../Components/Dropdown';
import { DropdownToggleType } from '../../../Components/Dropdown/dropdown-constants';
import { DynamicModelOverview } from '../../../Components/DynamicModelOverview/DynamicModelOverview';
import { InfoTooltip } from '../../../Components/InfoTooltip';
import { Spinner } from '../../../Components/Spinner';
import { WidgetContainer } from '../../../Components/WidgetContainer';
import { SetError } from '../../../redux/modals';
import { Brand25 } from '../../../utils/colors';
import { DATE_GROUPING_OPTIONS, dateToMddyy } from '../../../utils/date-utils';
import { formatAxisSpend, formatSpend, FullForecastSelection } from '../forecasting-constants';
import {
  formatForecastViewLabel,
  formatModelOverviewData,
  getKpiAxisFormatter,
  getKpiFormatter
} from '../forecasting-utils';

interface ModelResultsProps {
  trainTestData: TrainTestData[] | undefined;
  modelSummaryData: ForecastModelSummaryData | undefined;
  forecast: FullForecastSelection;
  showWIPFeatures?: boolean;
  chartDateGrouping: DateGroupingKey;
  setChartDateGrouping: (newVal: DateGroupingKey) => void;
  setError: SetError;
}

const ModelResults = ({
  forecast,
  trainTestData,
  modelSummaryData,
  showWIPFeatures = false,
  chartDateGrouping,
  setChartDateGrouping,
  setError
}: ModelResultsProps): ReactElement => {
  const [kpiChartYAxisWidth, setKpiChartYAxisWidth] = useState(0);
  const [spendChartYAxisWidth, setSpendChartYAxisWidth] = useState(0);

  const maxYAxisWidth = useMemo(
    () => Math.max(kpiChartYAxisWidth, spendChartYAxisWidth),
    [kpiChartYAxisWidth, spendChartYAxisWidth]
  );

  const formatKpi = useMemo(() => getKpiFormatter(forecast.outcomeVar), [forecast]);

  const formatAxisKpi = useMemo(() => getKpiAxisFormatter(forecast.outcomeVar), [forecast]);

  // Provide an explicit, downsampled set of x-axis ticks so the Actual vs Predicted chart's
  // fallback (non-monthly) axis renders a deterministic tick cadence instead of relying on
  // recharts' default tick selection, which changed across the v2 -> v3 major upgrade.
  const xAxisTicks = useMemo(() => {
    if (!trainTestData || trainTestData.length === 0) {
      return undefined;
    }
    const dates = trainTestData.map(row => row.date);
    const MAX_TICKS = 8;
    if (dates.length <= MAX_TICKS) {
      return dates;
    }
    const step = Math.ceil(dates.length / MAX_TICKS);
    const ticks = dates.filter((_, index) => index % step === 0);
    const lastDate = dates[dates.length - 1];
    if (ticks[ticks.length - 1] !== lastDate) {
      ticks.push(lastDate);
    }
    return ticks;
  }, [trainTestData]);

  return (
    <WidgetContainer
      collapsible
      header={`How well does the model understand our ${getCombinedChannelPlatform(
        forecast.channel,
        forecast.platform
      )} business?`}
      subHeader={
        <>
          High out-of-sample prediction accuracy is indicative of reliable causal understanding.
          <InfoTooltip rightLabel='More info' size={Size.SM}>
            The model is estimated by splitting up the time series data into slices called
            &quot;train&quot; and &quot;test.&quot; The training data is used to estimate the
            relationships between inputs (media investments, contextual variables) and outputs (the
            KPI). The test data is used to evaluate the performance of the trained model &quot;out
            of sample,&quot; i.e. on observations it has not already encountered. We use time series
            cross-validation with an expanding training window to robustly evaluate the model&apos;s
            performance. Results are summarized by the median across all validation folds, as well
            as the performance on the final fold.
            <br />
            <br />
            Out-of-sample predictive accuracy is a critical concept because it proxies what we
            ultimately care about, namely an understanding of likely KPI outputs given a combination
            of novel media inputs. See Last Fold wMAPE/MAE at right for a measure of this concept.
          </InfoTooltip>
        </>
      }
    >
      {!trainTestData || trainTestData.length === 0 || !modelSummaryData ? (
        <div className='forecasting-widget-loader model-results-loader'>
          <Spinner size={50} />
        </div>
      ) : (
        <div className='forecasting-model-overview'>
          <div className='left'>
            <ChartContainer
              rightActions={
                showWIPFeatures ? (
                  <DownloadDropdown size={Size.SM} design={Design.BRAND} onClickOptions={[]} />
                ) : undefined
              }
              title={
                <>
                  {showWIPFeatures && (
                    <Dropdown
                      overlayPlacement={Placement.RIGHT}
                      size={Size.LG}
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={chartDateGrouping}
                      options={DATE_GROUPING_OPTIONS}
                      onChange={option => {
                        setChartDateGrouping(option);
                        setError({
                          message: 'Updating date grouping of forecasting charts not yet supported'
                        });
                      }}
                    />
                  )}
                  <div>
                    Historical {forecast.channel}{' '}
                    {formatForecastViewLabel(forecast.outcomeVar, forecast.kpiName)}
                  </div>
                  <div className='header-annotation'>(Actual vs Predicted)</div>
                </>
              }
            >
              <ActualVsPredicted
                data={trainTestData}
                dateGrouping={chartDateGrouping}
                legendValue={formatForecastViewLabel(forecast.outcomeVar, forecast.kpiName)}
                syncId={1}
                tooltipFormatter={formatKpi}
                yAxisFormatter={formatAxisKpi}
                yAxisStaticWidth={maxYAxisWidth}
                setChartYAxisDynamicWidth={setKpiChartYAxisWidth}
                shouldSortTooltipItems={false}
                canUseMonthlyTicks
                xAxisTicks={xAxisTicks}
                tooltipProps={{
                  decorateLabelsWithColon: false
                }}
              />
            </ChartContainer>
            <ChartContainer
              rightActions={
                showWIPFeatures ? (
                  <DownloadDropdown size={Size.SM} design={Design.BRAND} onClickOptions={[]} />
                ) : undefined
              }
              title={
                <>
                  {showWIPFeatures && (
                    <Dropdown
                      overlayPlacement={Placement.RIGHT}
                      size={Size.LG}
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={chartDateGrouping}
                      options={DATE_GROUPING_OPTIONS}
                      onChange={option => {
                        setChartDateGrouping(option);
                        setError({
                          message: 'Updating date grouping of forecasting charts not yet supported'
                        });
                      }}
                    />
                  )}
                  <div>Historical {forecast.channel} Spend</div>
                </>
              }
            >
              <AreaChart
                data={trainTestData}
                areas={[
                  {
                    dataKey: 'spend',
                    incrementality: true,
                    name: `${forecast.channel} Spend`
                  }
                ]}
                dateGrouping={chartDateGrouping}
                xAxisDataKey='date'
                xAxisTickFormatter={val => dateToMddyy(val)}
                yAxisTickFormatter={val => formatAxisSpend(val)}
                tooltipFormatter={val => formatSpend(val)}
                yAxisStaticWidth={maxYAxisWidth}
                setChartYAxisDynamicWidth={setSpendChartYAxisWidth}
                hardCodedColorName={`${forecast.channel} Spend`}
                hardCodedColor={Brand25}
                incrementality={false}
                syncId={1}
                canUseMonthlyTicks
                tooltipProps={{
                  decorateLabelsWithColon: false
                }}
              />
            </ChartContainer>
          </div>
          <div className='right'>
            <DynamicModelOverview
              sections={formatModelOverviewData(
                modelSummaryData,
                forecast.outcomeVar,
                forecast.kpiName,
                forecast.primaryBudget,
                forecast.endDate
              )}
              rightActions={
                showWIPFeatures ? (
                  <DownloadDropdown
                    size={Size.SM}
                    design={Design.BRAND}
                    onClickOptions={[]}
                    menuOptions={['XLSX', 'CSV']}
                  />
                ) : undefined
              }
              height={835}
            />
          </div>
        </div>
      )}
    </WidgetContainer>
  );
};

export { ModelResults };
